<template>
  <v-app class="booking-widget-container">
    <main class="appointment-booking-widget">
      <h3
        v-if="!isStepCompleted"
        class="appointment-booking-widget__title"
      >
        Запись на прием
      </h3>
      <form
        v-show="currentStep === $options.stepSelectDoctor"
        class="booking-form"
        @submit.prevent
      >
        <div>
          <input-autocomplete
            :model-value="doctorId"
            title="Врач"
            :options="doctorList"
            @update:model-value="onUpdateDoctor"
          >
            <template #selection="{ item }">
              <item-employee :employee="item.raw" />
            </template>
            <template #item="{ props, item }">
              <item-employee
                v-bind="props"
                :employee="item.raw"
              />
            </template>
          </input-autocomplete>
          <date-picker
            v-model="selectedDate"
            :available-dates="workDays"
          />
          <div class="booking-form__time-slots">
            <button
              v-for="({ slot, title, selected }) in timeSlots"
              :key="title"
              class="booking-form__slot"
              :class="{ selected: selected }"
              type="button"
              @click="onSelectSlot(slot)"
            >
              {{ title }}
            </button>
          </div>
          <button
            ref="submitButton"
            class="booking-form__button-submit appointment-booking-widget__button"
            type="button"
            :disabled="isBookingButtonDisabled"
            @click="onSubmitSelectDoctor"
          >
            Записаться к врачу
          </button>
        </div>
      </form>
      <form
        v-if="currentStep === $options.stepInputPatient"
        class="patient-form"
        @submit.prevent
      >
        <button
          class="patient-form__back"
          type="button"
          @click="onBackToStepSelectDoctor"
        >
          Назад
        </button>
        <dl class="patient-form__info">
          <div>
            <dt class="patient-form__info-term">
              Дата и время приема:
            </dt>
            <dd>{{ selectedDateTime }}</dd>
          </div>
          <div>
            <dt class="patient-form__info-term">
              Врач:
            </dt>
            <dd> {{ selectedDoctorName }}</dd>
          </div>
          <div>
            <dt class="patient-form__info-term">
              Адрес:
            </dt>
            <dd>{{ clinicAddress }}</dd>
          </div>
        </dl>
        <input-text
          v-model="patientSurname"
          v-letter-capital
          placeholder="Фамилия"
          :violations="patientSurnameInputErrors"
        />
        <input-text
          v-model="patientName"
          v-letter-capital
          placeholder="Имя"
          :violations="patientNameInputErrors"
        />
        <input-text
          v-model="patientPatronymic"
          v-letter-capital
          placeholder="Отчество"
          :violations="patientPatronymicInputErrors"
        />
        <input-phone
          v-model="patientPhone"
          placeholder="Телефон"
          :violations="patientPhoneInputErrors"
        />
        <button
          class="patient-form__button-submit appointment-booking-widget__button"
          :disabled="isPatientButtonDisabled"
          type="button"
          @click="onSubmitInputPatient"
        >
          Записаться
        </button>
        <p
          v-if="errorTitle"
          class="patient-form__violation text__violation"
          role="alert"
        >
          {{ errorTitle }}
          <template v-if="clinicPhoneNumber">
            Запишитесь по телефону
            <a :href="`tel:${clinicPhoneNumber}`">{{ clinicPhoneNumber }}</a>
          </template>
        </p>
        <div class="patient-form__agreement">
          <input-checkbox
            v-model="isConsentReceived"
            class="form-registration__checkbox"
          />
          <div>
            Я даю согласие на <a
              class="auth-page__link"
              target="_blank"
              href="https://app.32top.ru/soglasie-na-obrabotku-personalnyh-dannyh"
            >обработку персональных данных
            </a> и&nbsp;принимаю <a
              class="auth-page__link"
              target="_blank"
              href="https://app.32top.ru/privacy-policy"
            >политику в&nbsp;отношении обработки пользовательских данных</a>
          </div>
        </div>
      </form>
      <form
        v-else-if="currentStep === $options.stepOrderConfirmation"
        class="confirmation-form"
        @submit.prevent="onSubmitConfirmation"
      >
        <span v-if="confirmationMethod === $options.confirmationMethodCall">
          Сейчас позвоним вам. Прослушайте сообщение и введите код из 4 цифр.
        </span>
        <span v-else-if="confirmationMethod === $options.confirmationMethodSms">
          Отправили код из 4 цифр в SMS. Введите его ниже и подтвердите запись.
        </span>
        <input-text
          v-model="confirmationCode"
          title="Код"
          name="code"
          required
          maxlength="4"
          pattern="\d{4}"
          inputmode="numeric"
          :violations="confirmationCodeInputErrors"
        />
        <template v-if="isShowResendConfirmationCodeButton">
          <div
            v-if="resendConfirmationSeconds > 0"
            class="confirmation-form__text-info"
          >
            Отправить код по смс {{ resendConfirmationSeconds }} сек
          </div>
          <button
            v-else
            class="confirmation-form__button-sms"
            type="button"
            :disabled="isAwaitRequest"
            @click="onResendConfirmationCode"
          >
            Отправить код по смс
          </button>
        </template>
        <div class="confirmation-form__wrapper">
          <button
            class="confirmation-form__button-submit appointment-booking-widget__button"
            type="submit"
          >
            Подтвердить
          </button>
          <p
            v-if="errorTitle"
            class="text__violation"
            role="alert"
          >
            {{ errorTitle }}
            <template v-if="clinicPhoneNumber">
              Запишитесь по телефону
              <a :href="`tel:${clinicPhoneNumber}`">{{ clinicPhoneNumber }}</a>
            </template>
          </p>
        </div>
      </form>
      <div
        v-else-if="isStepCompleted"
        class="appointment-booking-widget__completed completed-info"
      >
        <section class="completed-info__text">
          <p class="completed-info__title">
            Спасибо! Заявка отправлена
          </p>
          <p>Мы позвоним вам в&nbsp;ближайшее время для&nbsp;подтверждения записи</p>
        </section>
        <section class="completed-info__data-appoitment">
          <dl class="patient-appoitment-info">
            <div class="patient-appoitment-info__wrapper">
              <dt class="patient-appoitment-info__term">
                Дата и время приема:
              </dt>
              <dd class="patient-appoitment-info__desc">
                {{ selectedDateTime }}
              </dd>
            </div>
            <div class="patient-appoitment-info__wrapper">
              <dt class="patient-appoitment-info__term">
                Врач:
              </dt>
              <dd class="patient-appoitment-info__desc">
                {{ selectedDoctorName }}
              </dd>
            </div>
            <div class="patient-appoitment-info__wrapper">
              <dt class="patient-appoitment-info__term">
                Адрес:
              </dt>
              <dd
                class="patient-appoitment-info__desc"
                :title="clinicAddress"
              >
                {{ clinicAddress }}
              </dd>
            </div>
          </dl>
        </section>
      </div>
    </main>
  </v-app>
</template>

<script>
import DatePicker from '@/components/DatePicker.vue';
import InputAutocomplete from '@/components/InputAutocomplete.vue';
import InputCheckbox from '@/components/InputCheckbox.vue';
import InputText from '@/components/InputText.vue';
import InputPhone from '@/components/InputPhone.vue';
import ItemEmployee from '@/components/ItemEmployee.vue';
import { CreateAppointmentRequest } from '@/storage/gateways/AppointmentGateway';

const dateToIso = (date) => {
  const pad = (num, length = 2) => String(num).padStart(length, '0');
  const year = date.getFullYear();
  const month = pad(date.getMonth() + 1);
  const day = pad(date.getDate());
  return `${year}-${month}-${day}`;
};

const formattedDate = (dateTime) => {
  const [isoDate, time] = dateTime.split(' ');
  const date = isoDate.split('-').reverse().join('.');
  return `${date} ${time}`;
};

const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

const PERSON_NAME_PATTERN = /^[а-яА-ЯёЁ)(\- ]{1,255}$/;
/**
 * @description Валидное имя человека.
 * @param {string} name
 * @returns {boolean}
 */
const isValidPersonName = (name) => PERSON_NAME_PATTERN.test(name);

const STEP_SELECT_DOCTOR = 'select-doctor';
const STEP_INPUT_PATIENT = 'input-patient';
const STEP_ORDER_CONFIRMATION = 'order-confirmation';
const STEP_COMPLETED = 'completed';

const RESEND_CONFIRMATION_SECONDS = 60;

export default {
  confirmationMethodCall: 'call',
  confirmationMethodSms: 'sms',
  stepSelectDoctor: STEP_SELECT_DOCTOR,
  stepInputPatient: STEP_INPUT_PATIENT,
  stepOrderConfirmation: STEP_ORDER_CONFIRMATION,
  stepCompleted: STEP_COMPLETED,
  components: {
    DatePicker,
    InputAutocomplete,
    InputCheckbox,
    InputPhone,
    InputText,
    ItemEmployee,
  },
  data() {
    return {
      activeTimeSlot: null,
      clinicAddress: '',
      confirmationCode: '',
      confirmationId: '',
      confirmationMethod: '',
      currentStep: STEP_SELECT_DOCTOR,
      doctorId: null,
      doctorList: [],
      errorTitle: '',
      inputErrors: {},
      isAwaitRequest: false,
      isConsentReceived: false,
      isShowResendConfirmationCodeButton: true,
      patientName: '',
      patientPatronymic: '',
      patientPhone: '',
      patientSurname: '',
      resendConfirmationSeconds: 0,
      resendConfirmationTimer: null,
      selectedDate: null,
      selectedDoctor: null,
      workDayList: [],
    };
  },
  computed: {
    selectedDoctorName() {
      return this.selectedDoctor ? this.selectedDoctor.title : '';
    },
    confirmationCodeInputErrors: resolveInputErrors('code'),
    patientNameInputErrors: resolveInputErrors('patient-name'),
    patientPatronymicInputErrors: resolveInputErrors('patient-patronymic'),
    patientPhoneInputErrors: resolveInputErrors('patient-phone'),
    patientSurnameInputErrors: resolveInputErrors('patient-surname'),
    workDays() {
      return this.workDayList.map(({ date }) => date);
    },
    selectedDateIso() {
      if (this.selectedDate !== null) {
        return dateToIso(this.selectedDate);
      }
      return null;
    },
    timeSlotOptions() {
      if (this.selectedDateIso === null) {
        return [];
      }
      const workDay = this.workDayList.find((candidate) => candidate.date === this.selectedDateIso);
      if (workDay) {
        return workDay.slots;
      }
      return [];
    },
    timeSlots() {
      return this
        .timeSlotOptions
        .map((slot) => ({ slot, selected: this.activeTimeSlot === slot, title: slot.title }));
    },
    isBookingButtonDisabled() {
      return this.doctorId === null || this.activeTimeSlot === null;
    },
    isStepCompleted() {
      return this.currentStep === this.$options.stepCompleted;
    },
    isPatientButtonDisabled() {
      return this.isConsentReceived === false || this.isAwaitRequest;
    },
    selectedDateTime() {
      if (this.activeTimeSlot) {
        return formattedDate(this.activeTimeSlot.starts_at);
      }
      return '';
    }
  },
  created() {
    this
      .$doctorGateway
      .viewList()
      .then((result) => result
        .forEach((response) => {
          this.clinicAddress = response.clinic_address;
          this.doctorList = response.doctors;
          this.doctorList.slice(0, 1).forEach(({ value }) => {
            this.onUpdateDoctor(value);
          });
        }),
      );
  },
  unmounted() {
    this.stopResendConfirmationTimer();
  },
  methods: {
    onBackToStepSelectDoctor() {
      this.currentStep = STEP_SELECT_DOCTOR;
    },
    stopResendConfirmationTimer() {
      if (this.resendConfirmationTimer) {
        clearInterval(this.resendConfirmationTimer);
      }
    },
    onSelectSlot(slot) {
      this.activeTimeSlot = slot;
      this.$refs.submitButton.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
    },
    onUpdateDoctor(doctorId) {
      this.doctorId = doctorId;
      this.activeTimeSlot = null;
      this
        .$doctorGateway
        .viewAppointmentSlots(doctorId)
        .then((result) => result.forEach((response) => {
          this.workDayList = response.work_days;
        }));
    },
    onSubmitSelectDoctor() {
      this.selectedDoctor = this.doctorList.find(({ value }) => this.doctorId === value);
      this.currentStep = STEP_INPUT_PATIENT;
    },
    validateInputPatient() {
      const inputErrors = {};
      if (this.patientName.length === 0) {
        inputErrors['patient-name'] = ['Поле не должно быть пустым'];
      } else if (isValidPersonName(this.patientName) !== true) {
        inputErrors['patient-name'] = ['Некорректный формат'];
      }
      if (this.patientSurname.length === 0) {
        inputErrors['patient-surname'] = ['Поле не должно быть пустым'];
      } else if (isValidPersonName(this.patientSurname) !== true) {
        inputErrors['patient-surname'] = ['Некорректный формат'];
      }
      if (this.patientPatronymic.length && isValidPersonName(this.patientPatronymic) !== true) {
        inputErrors['patient-patronymic'] = ['Некорректный формат'];
      }
      if (this.patientPhone.length === 0) {
        inputErrors['patient-phone'] = ['Поле не должно быть пустым'];
      }
      return inputErrors;
    },
    onSubmitInputPatient() {
      this.errorTitle = '';
      this.clinicPhoneNumber = '';
      this.inputErrors = this.validateInputPatient();
      if (Object.keys(this.inputErrors).length) {
        return;
      }
      this.isAwaitRequest = true;
      const request = new CreateAppointmentRequest(
        this.doctorId,
        this.patientName,
        this.patientPatronymic,
        this.patientPhone,
        this.patientSurname,
        this.activeTimeSlot.starts_at,
      );
      const onSuccess = (response) => {
        this.currentStep = STEP_ORDER_CONFIRMATION;
        this.inputErrors = {};
        this.errorTitle = '';
        this.clinicPhoneNumber = '';
        this.confirmationCode = '';
        this.confirmationId = response.confirmation_id;
        this.confirmationMethod = response.confirmation_method;
        this.resendConfirmationSeconds = RESEND_CONFIRMATION_SECONDS;
        this.isShowResendConfirmationCodeButton = true;
        this.resendConfirmationTimer = setInterval(() => {
          this.resendConfirmationSeconds -= 1;
          if (this.resendConfirmationTimer === 0) {
            clearInterval(this.resendConfirmationTimer);
          }
        }, 1000);
      };
      const onFail = (error) => {
        const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs);
        this.errorTitle = failurePairs.length ? '' : error.title;
        this.clinicPhoneNumber = error.clinic_phone_number;
      };
      this
        .$appointmentGateway
        .create(request)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onSubmitConfirmation() {
      this.isAwaitRequest = true;
      const onFail = (error) => {
        const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs);
        this.errorTitle = failurePairs.length ? '' : error.title;
      };
      const onSuccess = () => {
        this.errorTitle = '';
        this.currentStep = STEP_COMPLETED;
        window.parent.postMessage({ type: 'mis32top_send' }, '*');
        this.stopResendConfirmationTimer();
      };
      this
        .$appointmentGateway
        .confirm(this.confirmationId, this.confirmationCode)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    onResendConfirmationCode() {
      this.isAwaitRequest = true;
      const onSuccess = (response) => {
        this.errorTitle = '';
        this.confirmationId = response.confirmation_id;
        this.resendConfirmationSeconds = RESEND_CONFIRMATION_SECONDS;
        this.resendConfirmationTimer = setInterval(() => {
          this.resendConfirmationSeconds -= 1;
          if (this.resendConfirmationTimer === 0) {
            clearInterval(this.resendConfirmationTimer);
          }
        }, 1000);
        this.confirmationMethod = this.$options.confirmationMethodSms;
        this.clinicPhoneNumber = '';
      };
      const onFail = (error) => {
        const failurePairs = error.causes.map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs);
        this.errorTitle = failurePairs.length ? '' : error.title;
        this.clinicPhoneNumber = error.clinic_phone_number;
      };
      this
        .$appointmentGateway
        .resendConfirmationCode(this.confirmationId)
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
  },
};
</script>


<style lang="scss">
@use "@/assets/style/mixins";

:root {
  --action-color: #0389e0;
  --border-color: #c2cfe0;
  --input-border-radius: 4px;
  --primary-background-color: #fff;
  --section-background-color: #f2f5f8;
  --unfocus-text: #5e6c80;
  --unfocus-color: #90a0b7;
  --warning-color: red;
}
.appointment-booking-widget {
  display: grid;
  justify-content: center;
  & &__title {
    margin-bottom: 10px;
    padding: 15px 20px 0;
    font-size: 24px;
    text-align: center;
  }
  & &__button {
    @include mixins.btn-blue(100%, 1.2rem);
    max-width: 220px;
    margin-inline: auto;
  }
}
.booking-form {
  width: 80dvw;
  max-width: 650px;
  max-height: 90dvh;
  padding: 20px;
  text-align: center;
  overflow-y: auto;
  & &__time-slots {
    display: grid;
    grid-auto-flow: dense;
    grid-template-columns: repeat(auto-fill, 130px);
    justify-content: space-between;
    gap: 8px;
    margin-block: 15px 25px;
    max-height: 200px;
    overflow-y: auto;
  }
  & &__slot {
    font-size: 16px;
    padding: 3px 6px;
    border-radius: 7px;
    border: 1px solid gray;
    &.selected {
      color: #ffffff;
      background-color: var(--action-color);
    }
  }
}

.patient-form {
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 15px;
  & &__back {
    color: var(--action-color);
    display: flex;
    align-items: center;
    gap: 7px;
    width: fit-content;
    &:before {
      content: '';
      background-image: url('@/assets/img/arrow-blue.svg');
      background-position: center;
      background-size: contain;
      width: 12px;
      height: 12px;
      display: block;
    }
    &:hover {
      text-decoration: underline;
    }
  }
  & &__info {
    display: grid;
    row-gap: 6px;
  }
  & &__info-term {
    font-weight: 600;
  }
  & &__violation {
    text-align: center;
  }
  & &__agreement {
    position: relative;
    display: flex;
    align-items: center;
    font-weight: 400;
    font-size: 14px;
    line-height: 14px;
    color: #707683;
    a {
      color: #707683 !important;
      &:hover {
        text-decoration: none;
      }
    }
  }
}

.confirmation-form {
  padding: 20px;
  display: flex;
  flex-direction: column;
  row-gap: 10px;
  & &__text-info {
    color: var(--unfocus-text);
  }
  & &__button-sms {
    align-self: flex-start;
    transition: color 0.3s;
    &:hover {
      color: var(--action-color);
    }
  }
  & &__wrapper {
    display: flex;
    flex-direction: column;
    align-items: center;
    row-gap: 10px;
  }
  & &__button-submit {
    margin-top: 10px;
  }
}

.completed-info {
  font-size: 18px;
  text-align: center;
  transform: translateY(50%);
  &__title {
    font-size: 26px;
    font-weight: 600;
    margin-bottom: 15px;
  }
  &__text {
    padding: 20px;
  }
  &__data-appoitment {
    padding: 20px;
    background-color: var(--section-background-color);
  }
}

.patient-appoitment-info {
  &__wrapper {
    display: flex;
    align-items: center;
    gap: 10px;
  }
  &__term {
    font-weight: 600;
  }
  &__desc {
    max-width: 85%;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.text {
  display: flex;
  flex-direction: column;
  &__label {
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    margin: 0 0 2px 0;
    color: var(--unfocus-color);
  }
  &__input {
    background: var(--primary-background-color);
    border: 1px solid var(--border-color);
    border-radius: var(--input-border-radius);
    outline: none;
    height: 40px;
    width: 100%;
    padding: 0 10px;
    appearance: none;
    font-size: 16px;
    color: var(--reading-color);
    font-weight: 400;
    &:focus {
      background-color: var(--primary-background-color);
      border: 2px solid var(--action-color);
      color: var(--reading-color);
      outline: 0;
    }
    &:disabled {
      background-color: var(--primary-background-color);
    }
  }
  &.invalid .text__input{
    color: var(--warning-color);
  }
  &__violation {
    color: var(--warning-color);
    font-size: 16px;
    line-height: 14px;
    font-weight: 400;
    padding: .125rem;
  }
}
.invalid {
  background: transparent !important;
}

@media screen and (max-width: 560px) {
  .booking-form {
    & &__time-slots {
      grid-template-columns: repeat(2, 1fr);
      max-height: 235px;
    }
  }
}

@media screen and (max-width: 385px) {
  .booking-form {
    & &__time-slots {
      grid-template-columns: 1fr;
      max-height: 115px;
    }
  }
}
</style>
